/**
 * RichiestaFornitorePublic — pagina PUBBLICA senza auth per fornitore.
 * Route: /richiesta-fornitore/:token
 * Mostra solo la richiesta identificata dal token e permette Accetta/Rifiuta.
 */
import React from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import axios from "axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const publicApi = axios.create({ baseURL: `${backendUrl}/api` });

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function RichiestaFornitorePublic() {
  const { token } = useParams();
  const qc = useQueryClient();
  const [nota, setNota] = React.useState("");
  const [choice, setChoice] = React.useState(null); // 'accetta'|'rifiuta'

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-richiesta", token],
    queryFn: () => publicApi.get(`/richieste-materiali/pubblico/${token}`).then((r) => r.data),
    retry: false,
  });

  const accetta = useMutation({
    mutationFn: () => publicApi.post(`/richieste-materiali/pubblico/${token}/accetta`, { nota }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(d.gia_risposto ? "Richiesta già accettata." : `Grazie! ${d.rate_generate} rate registrate.`);
      qc.invalidateQueries({ queryKey: ["public-richiesta", token] });
      setChoice(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore"),
  });
  const rifiuta = useMutation({
    mutationFn: () => publicApi.post(`/richieste-materiali/pubblico/${token}/rifiuta`, { nota }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(d.gia_risposto ? "Richiesta già rifiutata." : "Rifiuto registrato.");
      qc.invalidateQueries({ queryKey: ["public-richiesta", token] });
      setChoice(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore"),
  });

  if (isLoading) return <FullPage><Loader2 className="w-8 h-8 animate-spin text-slate-500" /></FullPage>;
  if (error || !data) return (
    <FullPage>
      <AlertTriangle className="w-12 h-12 text-rose-500 mb-3" />
      <h1 className="font-display font-black text-xl text-slate-900">Link non valido</h1>
      <p className="text-sm text-slate-600 mt-1">Il link potrebbe essere scaduto o revocato. Contatta l'azienda.</p>
    </FullPage>
  );

  const alreadyResponded = data.stato === "accettata" || data.stato === "rifiutata";
  const isAccepted = data.stato === "accettata";
  const isRejected = data.stato === "rifiutata";
  const pending = accetta.isPending || rifiuta.isPending;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full mb-3">
            <ShieldCheck className="w-4 h-4" /> Link sicuro · nessun login richiesto
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900">Richiesta fornitura</h1>
          <p className="text-sm text-slate-500">Riservata a <b>{data.fornitore_nome}</b></p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="p-5 bg-slate-900 text-white">
            <div className="text-xs uppercase tracking-widest text-slate-400">Richiesta n°</div>
            <div className="font-display font-black text-2xl">{data.numero_richiesta}</div>
            <div className="text-sm text-slate-300 mt-1">Cantiere: <b>{data.cantiere_nome}</b></div>
          </div>

          {isAccepted && (
            <div className="p-4 bg-emerald-50 border-b-2 border-emerald-300 flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" />
              <div>
                <div className="font-bold text-emerald-900">Richiesta accettata</div>
                <div className="text-xs text-emerald-800">Il {new Date(data.accepted_at).toLocaleString("it-IT")}</div>
              </div>
            </div>
          )}
          {isRejected && (
            <div className="p-4 bg-rose-50 border-b-2 border-rose-300 flex items-start gap-3">
              <XCircle className="w-6 h-6 text-rose-700" />
              <div>
                <div className="font-bold text-rose-900">Richiesta rifiutata</div>
                <div className="text-xs text-rose-800">Il {new Date(data.rejected_at).toLocaleString("it-IT")}</div>
              </div>
            </div>
          )}

          <div className="p-5 space-y-3 text-sm">
            <table className="w-full">
              <tbody className="divide-y divide-slate-100">
                <tr><td className="py-2 text-slate-500">Data richiesta</td><td className="py-2 text-right font-mono">{data.data_richiesta || (data.created_at || "").slice(0,10)}</td></tr>
                <tr><td className="py-2 text-slate-500">Condizioni pagamento</td><td className="py-2 text-right font-bold">{data.condizioni_pagamento}</td></tr>
                <tr><td className="py-2 text-slate-500">IVA</td><td className="py-2 text-right font-mono">{data.aliquota_iva}%</td></tr>
                <tr><td className="py-2 text-slate-500">Totale imponibile</td><td className="py-2 text-right font-black text-lg">{eur(data.totale_valore)}</td></tr>
              </tbody>
            </table>
            <div className="pt-2 border-t border-slate-200">
              <a href={`${backendUrl}/api/richieste-materiali/share/${token}/pdf`} target="_blank" rel="noreferrer"
                className="text-sm text-blue-700 hover:underline font-semibold">
                📄 Scarica dettaglio PDF completo →
              </a>
            </div>
          </div>

          {!alreadyResponded && !choice && (
            <div className="p-5 border-t border-slate-200 bg-slate-50 flex gap-2">
              <button onClick={() => setChoice("rifiuta")} data-testid="btn-public-rifiuta"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 border-2 border-rose-300 text-rose-700 hover:bg-rose-50 font-bold rounded-sm">
                <XCircle className="w-5 h-5" /> Rifiuta
              </button>
              <button onClick={() => setChoice("accetta")} data-testid="btn-public-accetta"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm">
                <CheckCircle2 className="w-5 h-5" /> Accetta
              </button>
            </div>
          )}

          {choice && !alreadyResponded && (
            <div className="p-5 border-t border-slate-200 space-y-3">
              <label className="text-xs font-bold uppercase text-slate-600 block">Nota (facoltativa)</label>
              <textarea rows={3} value={nota} onChange={(e) => setNota(e.target.value)}
                data-testid="input-nota-public" data-no-uppercase
                placeholder="Consegna prevista martedì · sconto 5% ecc."
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <div className="flex gap-2">
                <button onClick={() => setChoice(null)} disabled={pending}
                  className="px-4 py-2 border border-slate-300 text-slate-700 font-semibold rounded-sm">
                  Annulla
                </button>
                <div className="flex-1" />
                {choice === "accetta" ? (
                  <button onClick={() => accetta.mutate()} disabled={pending} data-testid="btn-conferma-public-accetta"
                    className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold rounded-sm">
                    <CheckCircle2 className="w-4 h-4" /> {pending ? "Invio…" : "Conferma accettazione"}
                  </button>
                ) : (
                  <button onClick={() => rifiuta.mutate()} disabled={pending} data-testid="btn-conferma-public-rifiuta"
                    className="inline-flex items-center gap-2 px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-sm">
                    <XCircle className="w-4 h-4" /> {pending ? "Invio…" : "Conferma rifiuto"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-4">
          Ethos Costruzioni · Il link è protetto da token univoco e non richiede login.
        </p>
      </div>
    </div>
  );
}

function FullPage({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
      {children}
    </div>
  );
}
